// Escanear sin señal, y que la lectura no se pierda · cierra la deuda AC-5.
//
// EL PROBLEMA. Los movimientos sobreviven a un pasillo sin cobertura desde
// que existe `cola.js`, pero para llegar al movimiento primero hay que
// RESOLVER el código —saber qué producto es—, y eso solo lo sabe el
// servidor. Sin señal, Escanear se quedaba en «no se pudo resolver» y la
// lectura se perdía: quien tenía la caja en la mano tenía que acordarse del
// código y volver a escanearlo después.
//
// QUÉ SE GUARDA Y QUÉ NO. Se guarda la LECTURA (el código leído), no el
// movimiento: sin saber qué producto es, de qué presentación ni cuántas
// unidades trae, no hay movimiento que armar sin inventar datos. Al volver
// la señal la lectura se manda sola al servidor; lo que contesta queda en
// el teléfono como una lista para terminar: «este código es tal producto,
// tócalo para registrar su movimiento». Y si el código no es de nada, el
// servidor lo anota en «Códigos que TEGO no reconoció» —eso SÍ es una
// escritura— y la lista dice qué hacer.
//
// LA MISMA FORMA QUE `cola.js` y `colaRecepciones.js`, a propósito: se
// guarda ANTES de nada, cada lectura lleva su clave de idempotencia (UUID
// v7) y el reenvío repite la clave. El servidor la recibe como `lectura` y
// no vuelve a sumar a `veces` si le llega dos veces la misma.
//
// UNA LECTURA PENDIENTE POR CÓDIGO. La misma caja leída tres veces sin
// señal es UNA lectura: lo que falta es saber qué es, y eso se pregunta
// una vez. Es la misma compactación que hace el servidor.
//
// LLEVA POR DÓNDE SE LEYÓ · U-02 fase b. La lectura guarda su `medio`
// (LECTOR, CAMARA, TECLADO o URL) junto al código: al abrirla con señal,
// el movimiento manda el mismo `medio_captura` que si se hubiera leído con
// conexión. El `codigo_escaneado` lo pone la respuesta del servidor, que se
// guarda entera.
//
// UN SOLO ENVÍO A LA VEZ. Lo pueden disparar la barra de conexión de la
// app y la propia pantalla de Escanear; si los dos corrieran a la vez,
// mandarían la misma lectura dos veces. El envío en curso se comparte.
import { api, ErrorApi } from '../api';
import { uuidv7 } from './uuidv7';
import { esUrl } from '../accesos/codigos';

const DB = 'tego-lecturas';
const STORE = 'lecturas';

export const PENDIENTE = 'PENDIENTE';   // guardada, sin mandar
export const RESUELTA = 'RESUELTA';     // el servidor dijo qué es
export const DESCONOCIDA = 'DESCONOCIDA'; // el servidor no la reconoce

function abrir() {
  return new Promise((res, rej) => {
    const r = indexedDB.open(DB, 1);
    r.onupgradeneeded = () => {
      if (!r.result.objectStoreNames.contains(STORE)) {
        r.result.createObjectStore(STORE, { keyPath: 'clave_idempotencia' });
      }
    };
    r.onsuccess = () => res(r.result);
    r.onerror = () => rej(r.error);
  });
}

function conStore(modo, fn) {
  return abrir().then((d) => new Promise((res, rej) => {
    const t = d.transaction(STORE, modo);
    const rq = fn(t.objectStore(STORE));
    t.oncomplete = () => res(rq && rq.result);
    t.onerror = () => rej(t.error);
    t.onabort = () => rej(t.error);
  }));
}

const guardar = (l) => conStore('readwrite', (s) => s.put(l));

/** Todas las lecturas guardadas, de la más vieja a la más nueva. */
export async function lecturas() {
  const todas = (await conStore('readonly', (s) => s.getAll())) || [];
  return todas.sort((a, b) => a._ts - b._ts);
}

export async function contarPendientesLectura() {
  return (await lecturas()).filter((l) => l.estado === PENDIENTE).length;
}

/** Quita una lectura de la lista (ya se abrió, o la persona la descarta). */
export const quitarLectura = (clave) => conStore('readwrite', (s) => s.delete(clave));

// ¿La petición no llegó a contestarse? Un `fetch` que falla no es un
// ErrorApi; un 502/503/504 lo devuelve el proxy cuando el nodo no está.
// Las dos cosas son «sin conexión con el servidor», no una respuesta.
//
// DIRECTRIZ-PWA F2 (11 oct 2026, parche del integrador para accesos; R-PWA-5
// y DISENO-F2 D-8): un 401 (sesión caducada), un 408/425/429 (el servidor
// pide esperar) o cualquier 5xx tampoco son una respuesta sobre el código.
// Antes un 401 al volver la señal marcaba la lectura como DESCONOCIDA con
// «sesión caducada» de motivo; ahora se queda PENDIENTE y se manda al
// volver a entrar, como el resto de las colas.
function noLlego(e) {
  if (!(e instanceof ErrorApi)) return true;
  return [0, 401, 408, 425, 429].includes(e.status) || e.status >= 500;
}

/**
 * Guarda una lectura para mandarla luego. Si ya hay una PENDIENTE del
 * mismo código, devuelve ésa: no se duplica.
 */
export async function guardarLectura(codigo, { tipo = null, medio = null } = {}) {
  const c = String(codigo || '').trim();
  // UNA URL ES UN QR AJENO y se compara TAL CUAL (auditoría U-02 b, H3): dos
  // enlaces cortos que solo difieren en mayúsculas son dos enlaces, y sin
  // señal el segundo se perdía fundido con el primero. Lo demás se sigue
  // juntando sin mirar mayúsculas, como hace el servidor.
  const url = esUrl(c);
  const mismo = (otro) => (url ? otro === c : otro.toUpperCase() === c.toUpperCase());
  const previa = (await lecturas())
    .find((l) => l.estado === PENDIENTE && mismo(l.codigo));
  if (previa) return previa;
  const l = {
    clave_idempotencia: uuidv7(), codigo: c, tipo: tipo || (url ? 'QR_AJENO' : null),
    medio, estado: PENDIENTE, _ts: Date.now(),
  };
  await guardar(l);
  return l;
}

/**
 * Resuelve un código. Devuelve:
 *   {estado:'resuelta', datos}   — el servidor contestó
 *   {estado:'guardada', lectura} — sin conexión: quedó en el teléfono
 * Lanza el ErrorApi si el servidor contesta que no (p. ej. código
 * desconocido): eso es una respuesta, no falta de señal.
 */
export async function resolverOGuardar(codigo, { tipo = null, medio = null } = {}) {
  const c = String(codigo || '').trim();
  if (globalThis.navigator?.onLine === false) {
    return { estado: 'guardada', lectura: await guardarLectura(c, { tipo, medio }) };
  }
  try {
    const datos = await api.get(`/qr/resolver?codigo=${encodeURIComponent(c)}`);
    return { estado: 'resuelta', datos };
  } catch (e) {
    if (noLlego(e)) {
      return { estado: 'guardada', lectura: await guardarLectura(c, { tipo, medio }) };
    }
    throw e;
  }
}

async function mandarPendientes() {
  let enviados = 0;
  for (const l of await lecturas()) {
    if (l.estado !== PENDIENTE) continue;
    try {
      const datos = await api.get(
        `/qr/resolver?codigo=${encodeURIComponent(l.codigo)}`
        + `&lectura=${encodeURIComponent(l.clave_idempotencia)}`);
      await guardar({ ...l, estado: RESUELTA, respuesta: datos, _mandada: Date.now() });
      enviados += 1;
    } catch (e) {
      if (noLlego(e)) {
        // SE PARA EN LA PRIMERA QUE NO LLEGA. Sin conexión, las demás
        // tampoco van a llegar, y seguir desordenaría los reenvíos.
        break;
      }
      // El servidor contestó que no: queda en la lista con su mensaje,
      // que dice qué pasó y qué hacer. Ya no se reintenta.
      await guardar({ ...l, estado: DESCONOCIDA, mensaje: e.message, _mandada: Date.now() });
      enviados += 1;
    }
  }
  return { enviados, restantes: await contarPendientesLectura() };
}

let enCurso = null;

/** Manda las lecturas pendientes. Devuelve {enviados, restantes}. */
export function sincronizarLecturas() {
  if (globalThis.navigator?.onLine === false) {
    return contarPendientesLectura().then((restantes) => ({ enviados: 0, restantes }));
  }
  if (!enCurso) {
    enCurso = mandarPendientes().finally(() => { enCurso = null; });
  }
  return enCurso;
}
